import React from 'react';
import {
  Shield, Layers, Users, Search, Monitor, Smartphone, Palette, Zap,
  Flame, Wallet, TrendingUp, Globe, Database, TestTube, Radio, ScanText, Plug,
  Workflow, Network,
} from 'lucide-react';
import type { IconType } from 'react-icons';
import {
  SiNodedotjs, SiGo, SiPython, SiTypescript, SiPhp, SiHtml5, SiCss,
  SiJavascript, SiDotnet, SiGooglecloud, SiDocker, SiGithub,
  SiServerless, SiFigma, SiWordpress, SiPostgresql, SiMongodb,
  SiStripe, SiWix, SiSquarespace, SiMysql, SiSharp,
  SiReact, SiNestjs, SiExpress, SiDjango, SiBootstrap, SiAstro,
  SiJquery, SiTypeorm, SiNextdotjs, SiVuedotjs, SiFastapi, SiAngular,
  SiTailwindcss, SiSocketdotio, SiRedis, SiGooglemaps,
} from 'react-icons/si';
import {
  DiPhotoshop, DiIllustrator, DiMsqlServer,
} from 'react-icons/di';
import { FaAws, FaMicrosoft } from 'react-icons/fa';

const ICON_SIZE = 16;

/**
 * Helper to render a react-icons IconType as a React element,
 * avoiding JSX component type mismatch with React 19 types.
 */
function si(Icon: IconType): React.ReactNode {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return React.createElement(Icon as any, { size: ICON_SIZE });
}

/**
 * Fallback icon: rounded rectangle with the first letter of the tech name.
 * Used only as edge-case fallback when no icon mapping exists.
 */
const LetterFallback: React.FC<{ name: string }> = ({ name }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: ICON_SIZE,
      height: ICON_SIZE,
      borderRadius: 4,
      background: 'linear-gradient(135deg, rgba(139,92,246,0.3), rgba(59,130,246,0.3))',
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1,
      color: 'var(--color-text-primary, #e0e0e0)',
      flexShrink: 0,
    }}
  >
    {name.charAt(0).toUpperCase()}
  </span>
);

/**
 * Map of tech name -> React icon element.
 *
 * Languages use their own branded logo.
 * Frameworks use their parent language's logo.
 * Tools/platforms use their own branded logo.
 * Items without a branded icon use lucide-react concept icons.
 */
const iconMap: Record<string, React.ReactNode> = {
  // ── Languages (own branded logo) ──────────────────────────────
  'Node.js':     si(SiNodedotjs),
  'Go':          si(SiGo),
  'Python':      si(SiPython),
  'TypeScript':  si(SiTypescript),
  'C#':          si(SiSharp),
  'PHP':         si(SiPhp),
  'Coldfusion':  <Flame size={ICON_SIZE} />,
  'HTML/CSS':    si(SiHtml5),
  'JavaScript':  si(SiJavascript),

  // ── Frameworks/Libraries (own branded logo) ───────────────────
  'React':          si(SiReact),
  'React Native':   si(SiReact),
  'jQuery':         si(SiJquery),
  'NestJS':         si(SiNestjs),
  'Express':        si(SiExpress),
  'HapiJS':         si(SiNodedotjs),
  'Django':         si(SiDjango),
  'ASP.NET':        si(SiDotnet),
  'Bootstrap':      si(SiBootstrap),
  'Astro':          si(SiAstro),
  'TypeORM':        si(SiTypeorm),
  'Next.js':        si(SiNextdotjs),
  'Vue.js':         si(SiVuedotjs),
  'FastAPI':        si(SiFastapi),
  'Angular':        si(SiAngular),
  'Tailwind CSS':   si(SiTailwindcss),

  // ── Cloud & DevOps (own branded logo) ─────────────────────────
  'AWS':              si(FaAws),
  'AWS Lambda':       si(FaAws),
  'Azure':            si(FaMicrosoft),
  'Google Cloud':     si(SiGooglecloud),
  'Docker':           si(SiDocker),
  'GitHub Actions':   si(SiGithub),
  'Serverless':       si(SiServerless),
  'GitHub Submodule': si(SiGithub),
  'SQS':              si(FaAws),
  'AWS ECS':          si(FaAws),
  'Lambda':           si(FaAws),
  'S3':               si(FaAws),
  'AWS (Chalice)':    si(FaAws),
  'LocalStack':       si(SiDocker),

  // ── Design Tools (own branded logo) ───────────────────────────
  'Figma':              si(SiFigma),
  'Adobe Photoshop':    si(DiPhotoshop),
  'Adobe Illustrator':  si(DiIllustrator),

  // ── Databases (own branded logo) ──────────────────────────────
  'PostgreSQL':  si(SiPostgresql),
  'MongoDB':     si(SiMongodb),
  'SQL Server':  si(DiMsqlServer),
  'MySQL':       si(SiMysql),
  'DynamoDB':    si(FaAws),

  // ── CMS/Builders ──────────────────────────────────────────────
  'WordPress':    si(SiWordpress),
  'Duda':         <Globe size={ICON_SIZE} />,
  'Wix':          si(SiWix),
  'Squarespace':  si(SiSquarespace),
  'GoHighLevel':  <TrendingUp size={ICON_SIZE} />,
  'DIVI':         si(SiWordpress),
  'Flatsome':     si(SiWordpress),

  // ── Other ─────────────────────────────────────────────────────
  'Code Review':              <Shield size={ICON_SIZE} />,
  'Clean Architecture':       <Layers size={ICON_SIZE} />,
  'Agile/Scrum':              <Users size={ICON_SIZE} />,
  'Stripe':                   si(SiStripe),
  'Momo':                     <Wallet size={ICON_SIZE} />,
  'SEO Optimization':         <Search size={ICON_SIZE} />,
  'Website Auditor':          <Monitor size={ICON_SIZE} />,
  'Responsive Design':        <Smartphone size={ICON_SIZE} />,
  'UI/UX Design':             <Palette size={ICON_SIZE} />,
  'Performance Optimization': <Zap size={ICON_SIZE} />,

  // ── Extra tech names from projects.ts ─────────────────────────
  'SQL':              <Database size={ICON_SIZE} />,
  'HTML':             si(SiHtml5),
  'CSS':              si(SiCss),
  'TDD':              <TestTube size={ICON_SIZE} />,
  'REST API':         <Radio size={ICON_SIZE} />,
  'Socket.IO':        si(SiSocketdotio),
  'Redis':            si(SiRedis),
  'Google Maps API':  si(SiGooglemaps),
  'Express.js':       si(SiExpress),
  'Golang':           si(SiGo),
  'OCR':              <ScanText size={ICON_SIZE} />,
  'OCPP':             <Plug size={ICON_SIZE} />,
  'AWS (ECS, Lambda, S3)': si(FaAws),
  'CI/CD':            <Workflow size={ICON_SIZE} />,
  'Microservices':    <Network size={ICON_SIZE} />,
};

interface TechIconProps {
  name: string;
}

const TechIcon: React.FC<TechIconProps> = ({ name }) => {
  const icon = iconMap[name];
  if (icon) return <>{icon}</>;
  return <LetterFallback name={name} />;
};

export default TechIcon;
